import {
  HarnessCatalog,
  HarnessPreferences,
  UpdateHarnessPreferencesRequest,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";
import { harnessCapabilitiesQueryKey } from "./harnessCapabilities";

export const harnessPreferencesQueryKey = ["harnessPreferences"] as const;

/** What this user's sessions start on when a create names nothing; "" where unset. */
export function useHarnessPreferences() {
  return useQuery<HarnessPreferences, AxiosError>({
    queryKey: harnessPreferencesQueryKey,
    queryFn: () => orgAPI.harnessPreferences.show(),
    staleTime: 5 * 60 * 1000,
  });
}

interface OptimisticContext {
  preferences: HarnessPreferences | undefined;
  catalog: HarnessCatalog | undefined;
}

/**
 * Patch the preferences. Optimistic on both caches that read them — the preference itself and
 * the catalog's resolved defaults — so the menu's check and every draft seeded from the default
 * move at once; a refusal rolls both back. Settling refetches both, since the server's resolution
 * (an alias, a cleared effort) is the truth.
 */
export function useUpdateHarnessPreferences(
  options?: UseMutationOptions<
    HarnessPreferences,
    AxiosError,
    UpdateHarnessPreferencesRequest,
    OptimisticContext
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    HarnessPreferences,
    AxiosError,
    UpdateHarnessPreferencesRequest,
    OptimisticContext
  >({
    mutationFn: (data) => orgAPI.harnessPreferences.update(data),
    onMutate: async (data) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: harnessPreferencesQueryKey }),
        queryClient.cancelQueries({ queryKey: harnessCapabilitiesQueryKey }),
      ]);
      const context: OptimisticContext = {
        preferences: queryClient.getQueryData(harnessPreferencesQueryKey),
        catalog: queryClient.getQueryData(harnessCapabilitiesQueryKey),
      };
      queryClient.setQueryData<HarnessPreferences>(
        harnessPreferencesQueryKey,
        (prev) => (prev ? mergePreferences(prev, data) : prev),
      );
      queryClient.setQueryData<HarnessCatalog>(
        harnessCapabilitiesQueryKey,
        (prev) => (prev ? resolveCatalog(prev, data) : prev),
      );
      return context;
    },
    onError: (_err, _data, context) => {
      if (!context) return;
      queryClient.setQueryData(harnessPreferencesQueryKey, context.preferences);
      queryClient.setQueryData(harnessCapabilitiesQueryKey, context.catalog);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: harnessPreferencesQueryKey });
      queryClient.invalidateQueries({ queryKey: harnessCapabilitiesQueryKey });
    },
    ...options,
  });
}

/** The server's merge: omitted keeps, null or "" clears, a harness change resets the rest. */
function mergePreferences(
  prev: HarnessPreferences,
  data: UpdateHarnessPreferencesRequest,
): HarnessPreferences {
  const next = { ...prev };
  if (data.default_harness !== undefined) {
    next.default_harness = data.default_harness ?? "";
    if (next.default_harness !== prev.default_harness) {
      next.default_model = "";
      next.default_reasoning_effort = "";
    }
  }
  if (data.default_model !== undefined) {
    next.default_model = data.default_model ?? "";
    if (next.default_model === "") next.default_reasoning_effort = "";
  }
  if (data.default_reasoning_effort !== undefined) {
    next.default_reasoning_effort = data.default_reasoning_effort ?? "";
  }
  return next;
}

/**
 * What the catalog would report once a patch naming the harness lands: that harness as the
 * default, its resolved model the patch's where the harness offers it (else the catalog's own,
 * since a harness change clears a stored model), the effort kept only where nothing moved. A
 * patch that leaves the harness alone is left to the refetch.
 */
function resolveCatalog(
  prev: HarnessCatalog,
  data: UpdateHarnessPreferencesRequest,
): HarnessCatalog {
  const harness = data.default_harness;
  const capability = prev.harness_capabilities.find((c) => c.id === harness);
  if (!harness || !capability?.available) return prev;
  const model =
    capability.models.find((m) => m.id === data.default_model)?.id ??
    capability.default_model_id;
  const unchanged =
    harness === prev.default_harness &&
    model === capability.resolved_default_model_id;
  return {
    default_harness: harness,
    harness_capabilities: prev.harness_capabilities.map((c) =>
      c.id !== harness
        ? c
        : {
            ...c,
            resolved_default_model_id: model,
            resolved_default_effort_id: unchanged
              ? c.resolved_default_effort_id
              : c.models.find((m) => m.id === model)?.default_effort_id,
          },
    ),
  };
}
